import React from "react";
import CommonDateTime from "../../Common/CommonDateTime";
import { formatChangeValue } from "../common/changeLabels";
import { formatBps, formatMinor, formatValidityMonths } from "../common/format";
import { PlaceholderBadge, StateBadge } from "./PlansHome";
import { cycleValueBasisLabel, modelName } from "./editor/draftModel";

// A plan version, read only. Amounts and percentages are the stored values, formatted.
// `models` is the website model list ({ slug, name }) used to name a segment's models; a slug it
// does not have is shown as it is.
const VersionContentView = ({ version, models = [] }) => {
  const { content } = version;
  const rule = content.pricingRule;
  // A version stored before app content existed has no display.
  const display = content.display || {};
  const hero = display.hero || null;
  const banner = display.upgradeBanner || null;
  const notCovered = (display.details && display.details.notCovered) || [];
  const description = display.details ? display.details.description : null;
  const segments = rule.segments && Array.isArray(rule.segments.items) ? rule.segments : null;
  const notSet = <span className="warranty-muted">Not set</span>;
  const textOr = (value) => (typeof value === "string" && value !== "" ? value : notSet);
  const money = (value) => formatMinor(value);
  const row = (label, value) => (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
  return (
    <div className="warranty-readonly">
      <dl>
        {row("State", <StateBadge state={version.state} />)}
        {row("Terms version", <>{version.termsVersion} {version.placeholderTerms && <PlaceholderBadge />}</>)}
        {row("Starts", CommonDateTime(version.effectiveFrom, "LL", "show"))}
        {row("Created", CommonDateTime(version.createdAt, "LL, h:mm A", "show"))}
        {version.cancelledAt && row("Schedule cancelled", CommonDateTime(version.cancelledAt, "LL, h:mm A", "show"))}
        {row("Display name", content.displayName)}
        {row("Tier rank", content.tierRank)}
        {row("Monthly tenures", content.tenures.length === 0 ? "None (one-time only)" : content.tenures.map((t) => `${t} months`).join(", "))}
      </dl>

      <h6>App content</h6>
      <dl>
        {row("Tagline", textOr(display.tagline))}
        {row("Best value badge", display.bestValue === true ? "Yes" : "No")}
        {row("Hero title", textOr(hero ? hero.title : null))}
        {row("Hero subtitle", textOr(hero ? hero.subtitle : null))}
        {row("Upgrade banner title", textOr(banner ? banner.title : null))}
        {row("Upgrade banner, monthly", textOr(banner ? banner.subtitleMonthly : null))}
        {row("Upgrade banner, one-time", textOr(banner ? banner.subtitleOneTime : null))}
        {row("Description", typeof description === "string" && description !== "" ? <span className="warranty-pre">{description}</span> : notSet)}
        {row(
          "Not covered",
          notCovered.length === 0 ? notSet : (
            <ul className="warranty-plain-list">
              {notCovered.map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </ul>
          )
        )}
        {row("Terms link", textOr(display.termsUrl))}
      </dl>

      <h6>Benefits</h6>
      <table className="warranty-changes">
        <thead>
          <tr>
            <th scope="col">Label</th>
            <th scope="col">App title and subtitle</th>
            <th scope="col">Kind</th>
            <th scope="col">Charged</th>
            <th scope="col">Term</th>
            <th scope="col">Activation</th>
            <th scope="col">Unlocks</th>
          </tr>
        </thead>
        <tbody>
          {content.benefits.map((benefit) => (
            <tr key={benefit.key}>
              <th scope="row">{benefit.label}</th>
              <td>
                {benefit.title || benefit.label}
                {benefit.subtitle && <div className="warranty-muted">{benefit.subtitle}</div>}
              </td>
              <td>{formatChangeValue("benefits.kind", benefit.kind)}</td>
              <td>{formatChangeValue("benefits.chargeModel", benefit.chargeModel)}</td>
              <td>{benefit.termMonths === null ? "-" : `${benefit.termMonths} months`}</td>
              <td>{formatChangeValue("benefits.activation", benefit.activation)}</td>
              <td>
                {formatChangeValue("benefits.unlock", benefit.unlock.type)}
                {benefit.unlock.type === "paid_months" ? `: ${benefit.unlock.months} months` : ""}
                {benefit.unlock.type === "months_after_start" ? `: ${benefit.unlock.months} months after purchase` : ""}
                {benefit.unlock.type === "date" ? `: ${benefit.unlock.on}` : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h6>Settings</h6>
      <dl>
        {row("Status", content.settings.inactive === true ? "Inactive (off sale)" : "Active")}
        {content.settings.cohort === "late" && row("Offered to", "Older bikes only")}
        {content.settings.offerEndsOn && row("Offer ends on", content.settings.offerEndsOn)}
        {row("Eligibility window", `${content.settings.eligibilityDays} days`)}
        {row("Upgrade window", `${content.settings.upgradeWindowDays} days`)}
        {row("Grace period", `${content.settings.graceDays} days`)}
        {row("Refund window", `${content.settings.refundWindowDays} days`)}
        {row("Refund needs the plan unused", content.settings.refundRequiresNoUse ? "Yes" : "No")}
        {row("Extension cover", formatChangeValue("settings.extensionGrant", content.settings.extensionGrant))}
        {row("Unpaid purchase expires after", `${content.settings.pendingExpiryHours} hours`)}
      </dl>

      <h6>Pricing</h6>
      <dl>
        {row("Share of MRP", formatBps(rule.baseBps))}
        {row("Floor", money(rule.floorMinor))}
        {row("Cap", money(rule.capMinor))}
        {row("Rounding", `step ${money(rule.roundingStepMinor)}, ending ${money(rule.roundingEndingMinor)}`)}
        {row("Monthly uplift", formatBps(rule.monthlyUpliftBps))}
        {row("Instalment rounding", `step ${money(rule.monthlyRoundingStepMinor)}, ending ${money(rule.monthlyRoundingEndingMinor)}`)}
        {Object.keys(rule.tenureFactors).map((months) => row(`Uplift for ${months} months`, formatBps(rule.tenureFactors[months])))}
        {Object.keys(rule.addonRates).map((key) =>
          row(
            `Add-on ${key}`,
            `${formatBps(rule.addonRates[key].bps)} of MRP, floor ${money(rule.addonRates[key].floorMinor)}, cap ${money(rule.addonRates[key].capMinor)}`
          )
        )}
        {rule.couponRule &&
          row(
            "Coupon rule",
            `${formatBps(rule.couponRule.bps)} of ${rule.couponRule.basis === "plan_price" ? "the plan price (incl. GST)" : "MRP"}, ${formatChangeValue("couponRule.mode", rule.couponRule.mode).toLowerCase()}, ${
              Number.isInteger(rule.couponRule.validityMonths)
                ? `valid for ${formatValidityMonths(rule.couponRule.validityMonths)}`
                : "validity not set (service default)"
            }`
          )}
        {row("Priced on", cycleValueBasisLabel(rule.cycleValueBasis))}
        {row("Tax rate", rule.taxRateBps === null ? "Market default" : formatBps(rule.taxRateBps))}
      </dl>

      <h6>Overrides</h6>
      {content.overrides.length === 0 ? (
        <p className="warranty-help" style={{ float: "none" }}>No overrides.</p>
      ) : (
        <dl>
          {content.overrides.map((override) =>
            row(
              override.productGroup,
              [
                override.prices.oneTimeMinor !== undefined ? `one-time ${money(override.prices.oneTimeMinor)}` : null,
                ...Object.keys(override.prices.monthly || {}).map((m) => `${m} months ${money(override.prices.monthly[m])}`),
                ...Object.keys(override.prices.addons || {}).map((k) => `${k} ${money(override.prices.addons[k])}`),
                override.prices.couponValueMinor !== undefined ? `coupon ${money(override.prices.couponValueMinor)}` : null,
              ]
                .filter(Boolean)
                .join(", ") || "No prices"
            )
          )}
        </dl>
      )}

      {segments !== null && (
        <>
          <h6>Bike segments</h6>
          <table className="warranty-changes">
            <thead>
              <tr>
                <th scope="col">Segment</th>
                <th scope="col">Models</th>
                <th scope="col">One-time price</th>
                <th scope="col">Dealer margin</th>
              </tr>
            </thead>
            <tbody>
              {segments.items.map((segment) => (
                <tr key={segment.key}>
                  <th scope="row">
                    {segment.name}
                    {segment.key === segments.defaultKey && <span className="warranty-muted"> (default)</span>}
                  </th>
                  <td>
                    {(segment.models || []).length === 0
                      ? "None"
                      : segment.models.map((slug) => modelName(models, slug)).join(", ")}
                  </td>
                  <td>{money(segment.prices ? segment.prices.oneTimeMinor : undefined)}</td>
                  <td>{money(segment.dealerMarginMinor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default VersionContentView;
